import { AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import classNames from 'classnames/bind';
import React, { useEffect, useMemo, useState } from 'react';

import { withPriceSeperator } from '@flight-workspace/common-client/src/util/number';
import { convertSecondsToHoursAndMinutes } from 'common/utils';
import { MinPriceFlight } from 'types/apollo';
import styles from './index.module.scss';
import { ILocation } from './types';

const cx = classNames.bind(styles);

interface AirportMarkerProps {
  lng: number;
  lat: number;
  currentLocation?: ILocation;
  featureId: string;
  property: MinPriceFlight;
  clickCode?: string;
  isStatic?: boolean;
  onClick?: (e: any, marker: MinPriceFlight) => void;
}

const MAX_Z_INDEX = 10_000;

const getPolyLineOptions = (
  start: { lat: number; lng: number },
  end: { lat: number; lng: number },
) => ({
  path: [start, end],
  geodesic: true,
  strokeColor: '#5398f5',
  strokeOpacity: 0,
  strokeWeight: 4,
  icons: [
    {
      icon: {
        path: 'M 0,-1 0,1',
        strokeOpacity: 1,
        scale: 3,
      },
      offset: '0',
      repeat: '10px',
    },
  ],
});

const AirportMarker: React.FC<AirportMarkerProps> = ({
  lng,
  lat,
  property,
  clickCode,
  isStatic = false,
  currentLocation,
  onClick,
}) => {
  const map = useMap();

  const position = useMemo(() => ({ lat, lng }), [lat, lng]);

  const [isHover, setIsHover] = useState(false);
  const clickProps = {
    onClick: () => onClick?.(null, property),
    ...(clickCode
      ? {
          'data-event-nlog': 'click',
          'data-event-area-code': clickCode,
        }
      : undefined),
  };

  const handleHover = () => {
    if (isStatic) {
      return;
    }
    setIsHover(true);
  };

  const handleUnHover = () => {
    if (isStatic) {
      return;
    }
    setIsHover(false);
  };

  useEffect(() => {
    if (!map) return;

    let polyline: google.maps.Polyline | null = null;

    if (isHover && currentLocation) {
      polyline = new google.maps.Polyline(
        getPolyLineOptions(
          { lat: currentLocation.latitude, lng: currentLocation.longitude },
          position,
        ),
      );
      polyline.setMap(map);
    }

    return () => {
      if (polyline) {
        polyline.setMap(null);
      }
    };
  }, [map, isHover, currentLocation, position]);

  return (
    <>
      <AdvancedMarker position={position} clickable={!isStatic}>
        <div
          className={cx('Tooltip', isStatic && 'static')}
          {...clickProps}
          onMouseOver={handleHover}
          onMouseLeave={handleUnHover}
        ></div>
      </AdvancedMarker>
      <AdvancedMarker
        position={position}
        collisionBehavior={
          google.maps.CollisionBehavior.OPTIONAL_AND_HIDES_LOWER_PRIORITY
        }
        clickable={!isStatic}
        zIndex={
          isHover
            ? MAX_Z_INDEX
            : MAX_Z_INDEX - property.arrivalLocation.popularity!
        }
      >
        <p
          className={cx('info', {
            static: isStatic,
            has_event_label: false,
          })}
          {...clickProps}
          onMouseOver={handleHover}
          onMouseLeave={handleUnHover}
        >
          <i className={cx('name')}>{property.arrivalLocation.cityName}</i>
          {false && <span className={cx('event')}>이벤트</span>}
          <b className={cx('price')}>
            {withPriceSeperator(property.minPrice)}~
          </b>
          <span className={cx('duration')}>
            {property.stops ? `경유 ${property.stops}회` : '직항'},&nbsp;
            {convertSecondsToHoursAndMinutes(property.duration)}
          </span>
        </p>
      </AdvancedMarker>
    </>
  );
};

export default AirportMarker;
